import * as React from "react";
import { useEffect, useMemo } from "react";
import { SmartApplyTemplate } from "../../../template/smart-apply/default-template";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useModal } from "../../../../utils/custom-hooks";
import { Loader } from "../../../organisms/common/loading";
import { formatDatetime } from "../../../../utils/date-utils";
import { parseQueryOfLocation } from "../../../../utils/url-utils";
import {
  CustomerReviewsContextProps,
  CustomerReviewsService,
} from "../../../../application/service/smart-apply/customer-reviews/customer-reviews-service";
import { connectPageService } from "@graffer-inc/smart-apply";
import { RBACHidden } from "../../../helpers/rbac/rbac";
import {
  InformationBanner,
  Link,
  Pagination,
  Table,
  TableBody,
  TableHeader,
  TableRow,
  Td,
  Th,
} from "@graffer-inc/console-design-kit";
import { SmartApplyMenuList } from "../../../organisms/smart-apply/menu-list";
import { CustomerReviewHeader } from "./contentsHeader";
import "./customer-reviews.scss";
import classnames from "classnames";
import { ReviewStars } from "./review-star";
import { SmartApplyR } from "../route";
import { matchType } from "../../../../application/domain/base/base-type";

export const CustomerReviewsIndex: React.FC<CustomerReviewsContextProps> = ({
  isLoading,
  customerReviews,
  paging,
  setPage,
  movePage,
  selectedProcedure,
  setDateRange,
  fetchCustomerReviews,
  searchCustomerReviews,
  onCSVDownload,
  onSelectProcedure,
}) => {
  const {
    page = "1",
    procedureId,
    from,
    to,
  } = parseQueryOfLocation(useLocation()) as {
    page: string | undefined;
    procedureId: string | undefined;
    from: string | undefined;
    to: string | undefined;
  };
  const [dateFrom, dateTo] = useMemo(() => {
    return [
      from === undefined ? undefined : new Date(Date.parse(from)),
      to === undefined ? undefined : new Date(Date.parse(to)),
    ];
  }, [from, to]);

  const menuToggle = useModal();

  useEffect(() => {
    setPage(parseInt(page, 10));
    setDateRange({
      from: dateFrom,
      to: dateTo,
    });
    onSelectProcedure(procedureId);
    fetchCustomerReviews(procedureId);
  }, [
    page,
    setPage,
    setDateRange,
    procedureId,
    onSelectProcedure,
    fetchCustomerReviews,
    dateFrom,
    dateTo,
  ]);

  return (
    <SmartApplyTemplate
      breadcrumbs={[{ to: "#", title: "申請者レビュー" }]}
      menuList={<SmartApplyMenuList />}
      contentsHeader={
        <CustomerReviewHeader
          selectedProcedure={selectedProcedure}
          setDateRange={setDateRange}
          dateFrom={dateFrom}
          dateTo={dateTo}
          searchCustomerReviews={searchCustomerReviews}
          onCSVDownload={onCSVDownload}
          onSelectProcedure={onSelectProcedure}
          menuToggle={menuToggle}
        />
      }
    >
      <RBACHidden requirePermissions={"customer_reviews.list"}>
        <Table wrapper={false} borderType="row">
          <TableHeader>
            <TableRow>
              <Th>手続き名称</Th>
              <Th>点数</Th>
              <Th>コメント</Th>
              <Th>回答日時</Th>
              <Th>{""}</Th>
            </TableRow>
          </TableHeader>
          <TableBody>
            <Loader isLoading={isLoading}>
              {customerReviews.map((cr) => (
                <TableRow key={cr.id}>
                  <Td>{cr.procedureName}</Td>
                  <Td>
                    <p className="customer-reviews-review">
                      {cr.answerRank}
                      <ReviewStars rank={cr.answerRank} />
                    </p>
                  </Td>
                  <Td width={"400px"}>{cr.comment}</Td>
                  <Td>{formatDatetime(cr.createdAt)}</Td>
                  {/* <Td>{convertToFormattedId(cr.applicationId)}</Td> */}
                  <Td whiteSpace="nowrap">
                    <Link
                      anchor={({ ...props }) => (
                        <RouterLink
                          {...props}
                          to={SmartApplyR.ApplicationDetail.link({
                            id: cr.applicationId,
                          })}
                        />
                      )}
                    >
                      申請詳細
                    </Link>
                  </Td>
                </TableRow>
              ))}
            </Loader>
          </TableBody>
        </Table>
        {!isLoading &&
          customerReviews.length === 0 &&
          matchType(
            !procedureId && !dateFrom && !dateTo
              ? "no_registered"
              : "no_results",
            {
              no_registered: (
                <div className="py-2">
                  <InformationBanner
                    message="申請者レビューがまだ登録されていません。"
                    description="初めての申請者レビューが登録されると、ここに表示されます。"
                  />
                </div>
              ),
              no_results: (
                <div className="py-2">
                  <InformationBanner
                    message="条件に該当する申請者レビューが見つかりませんでした。"
                    description="検索条件を確認してください。"
                  />
                </div>
              ),
            },
          )}
        {paging.total > 0 && (
          <div className={classnames("customer-reviews-pagination-footer")}>
            <Pagination
              onPageClick={movePage}
              currentPage={paging.current}
              lastPage={paging.total}
            />
          </div>
        )}
      </RBACHidden>
    </SmartApplyTemplate>
  );
};

export const CustomerReviewsContainer = connectPageService(
  CustomerReviewsIndex,
  () => new CustomerReviewsService(),
);
